import {
    STUDY_TABLE_COLUMN_ASSIGNED,
    STUDY_TABLE_COLUMN_SPOT,
    STUDY_TABLE_COLUMN_CONFIRMED,
    STUDY_TABLE_COLUMN_LOCATION,
    STUDY_TABLE_COLUMN_PAID,
    STUDY_TABLE_COLUMN_LAST_CONTACTED,
    STUDY_TABLE_COLUMN_NOTES
} from 'utils/constants';
import helpers from 'utils/helpers';
import helpersStudy from 'utils/helpers-study';

import {
    PANEL_PROPERTY_TYPE,
    PANEL_PROPERTY_TYPE_LABEL,
    namespaceColumnId,
    screenerSubLabel,
    STUDY_PROPERTY_SUBLABEL,
    CUSTOM_VARIABLE_SUBLABEL
} from './constants';

/**
 * Ordered list of built-in study-level columns.
 * Change the order here to change the default dropdown order before
 * the user has re-ordered anything via drag-drop.
 */
const BUILTIN_STUDY_COLUMNS = [
    STUDY_TABLE_COLUMN_ASSIGNED,
    STUDY_TABLE_COLUMN_SPOT,
    STUDY_TABLE_COLUMN_CONFIRMED,
    STUDY_TABLE_COLUMN_LOCATION,
    STUDY_TABLE_COLUMN_PAID,
    STUDY_TABLE_COLUMN_LAST_CONTACTED,
    STUDY_TABLE_COLUMN_NOTES
];

const isShowing = (hidden, id) => !(Array.isArray(hidden) && hidden.includes(id));

const asArray = v => (Array.isArray(v) ? v : []);

/**
 * Build the flat `tableColumns_all` list the Study Overview renders +
 * the NiceDropdown toggles.
 *
 * Pure: same inputs produce same output. Safe to call on every relevant
 * state change — output is always a fresh array.
 *
 * Panel properties are only included when the user has added them via
 * the modal (i.e. their id appears in `panelPropertyIds`). Non-added
 * panel properties are omitted entirely from the table/dropdown and
 * only show up in the ManageColumns modal as unchecked rows.
 *
 * @param {Object} params
 * @param {Object} [params.study]
 * @param {Array<import('services/services').CustomDataColumn>} [params.panelColumns]
 * @param {string[]} [params.tableColumnsHidden]
 * @param {number[]} [params.panelPropertyIds]
 * @returns {Array<{id:string,title:string,checked:boolean,type:string,subLabel:string,panelPropertyId?:number}>}
 */
export function buildTableColumns({ study, panelColumns, tableColumnsHidden, panelPropertyIds }) {
    const hidden = asArray(tableColumnsHidden);
    const panelIds = asArray(panelPropertyIds);
    const cols = [];

    // Built-in study columns.
    BUILTIN_STUDY_COLUMNS.forEach(def => {
        cols.push({
            id: def.id,
            title: def.title,
            checked: isShowing(hidden, def.id),
            type: 'study',
            subLabel: STUDY_PROPERTY_SUBLABEL
        });
    });

    // study.custom_sp_attributes — treat as study-level custom columns.
    if (study && Array.isArray(study.custom_sp_attributes)) {
        study.custom_sp_attributes.forEach(cspa => {
            const id = helpersStudy.getCspaTableColumnId(cspa);
            cols.push({
                id,
                title: cspa.title,
                checked: isShowing(hidden, id),
                type: 'study',
                subLabel: STUDY_PROPERTY_SUBLABEL
            });
        });
    }

    // Screener sections: Answered + non-static questions + Custom Variables.
    if (study && Array.isArray(study.screeners)) {
        study.screeners.forEach(screener => {
            const label = screenerSubLabel(screener.title);

            const answeredId = helpersStudy.getScreenerAnsweredTableColumnId(screener.id);
            cols.push({
                id: answeredId,
                title: `Answered: ${screener.title}`,
                checked: isShowing(hidden, answeredId),
                type: 'screener',
                subLabel: label
            });

            const questions = helpersStudy.getScreenerQuestions(screener);
            if (Array.isArray(questions)) {
                questions.forEach(question => {
                    if (question.static) return;
                    const qId = helpersStudy.getScreenerQuestionTableColumnId(question.field_name);
                    cols.push({
                        id: qId,
                        title: helpers.removeHtml(question.label),
                        checked: isShowing(hidden, qId),
                        type: 'screener',
                        subLabel: label
                    });
                });
            }

            const cvId = helpersStudy.getScreenerCustomVariabledTableColumnId(screener.id);
            cols.push({
                id: cvId,
                title: `Custom Variables: ${screener.title}`,
                checked: isShowing(hidden, cvId),
                type: 'screener',
                subLabel: CUSTOM_VARIABLE_SUBLABEL
            });
        });
    }

    // Panel properties — only those the user has opted into.
    if (Array.isArray(panelColumns) && panelColumns.length && panelIds.length) {
        const byId = new Map(panelColumns.map(p => [Number(p.id), p]));
        panelIds.forEach(id => {
            const meta = byId.get(Number(id));
            if (!meta) return; // property may have been deleted from the panel
            const colId = namespaceColumnId(id);
            cols.push({
                id: colId,
                title: meta.title,
                checked: isShowing(hidden, colId),
                type: PANEL_PROPERTY_TYPE,
                subLabel: PANEL_PROPERTY_TYPE_LABEL,
                panelPropertyId: id
            });
        });
    }

    return cols;
}

/**
 * Build the grouped sections used by the ManageColumns modal.
 *
 * The modal shows *every* possible column (including panel properties
 * the user hasn't added yet) so they can opt in/out from one place. It's
 * the union of:
 *
 *   1. Study Properties (built-ins + custom_sp_attributes)
 *   2. Screener: <title> — one section per screener
 *   3. Each panel property group's properties — ordered by group.order
 *      then property.order. Properties whose group was deleted go into
 *      an "Other" fallback section.
 *
 * For each item, `checked` reflects what the user has right now:
 *   - non-panel-property: NOT in `tableColumnsHidden`
 *   - panel-property: in `panelPropertyIds`
 *
 * Saving the modal collapses these back into the two storage keys.
 *
 * @returns {Array<{id:string,name:string,items:Array<{id:string,title:string,checked:boolean,type:string,panelPropertyId?:number}>}>}
 */
export function buildAllColumnSections({
    study,
    panelColumns,
    panelPropertiesGroups,
    tableColumnsHidden,
    panelPropertyIds
}) {
    const hidden = asArray(tableColumnsHidden);
    const panelIdsSet = new Set(asArray(panelPropertyIds).map(Number));
    const isNonPanelChecked = id => !hidden.includes(id);

    const sections = [];

    // --- Study Properties ---
    const studyItems = BUILTIN_STUDY_COLUMNS.map(def => ({
        id: def.id,
        title: def.title,
        checked: isNonPanelChecked(def.id),
        type: 'study'
    }));
    if (study && Array.isArray(study.custom_sp_attributes)) {
        study.custom_sp_attributes.forEach(cspa => {
            const id = helpersStudy.getCspaTableColumnId(cspa);
            studyItems.push({
                id,
                title: cspa.title,
                checked: isNonPanelChecked(id),
                type: 'study'
            });
        });
    }
    if (studyItems.length) {
        sections.push({ id: 'study', name: 'Study Properties', items: studyItems });
    }

    // --- Per-screener sections ---
    if (study && Array.isArray(study.screeners)) {
        study.screeners.forEach(screener => {
            const items = [];

            const answeredId = helpersStudy.getScreenerAnsweredTableColumnId(screener.id);
            items.push({
                id: answeredId,
                title: `Answered: ${screener.title}`,
                checked: isNonPanelChecked(answeredId),
                type: 'screener'
            });

            const questions = helpersStudy.getScreenerQuestions(screener);
            if (Array.isArray(questions)) {
                questions.forEach(question => {
                    if (question.static) return;
                    const qId = helpersStudy.getScreenerQuestionTableColumnId(question.field_name);
                    items.push({
                        id: qId,
                        title: helpers.removeHtml(question.label),
                        checked: isNonPanelChecked(qId),
                        type: 'screener'
                    });
                });
            }

            const cvId = helpersStudy.getScreenerCustomVariabledTableColumnId(screener.id);
            items.push({
                id: cvId,
                title: `Custom Variables: ${screener.title}`,
                checked: isNonPanelChecked(cvId),
                type: 'screener'
            });

            if (items.length) {
                sections.push({
                    id: `screener_${screener.id}`,
                    name: `Screener: ${screener.title}`,
                    items
                });
            }
        });
    }

    // --- Panel property groups ---
    const panelProps = asArray(panelColumns);
    if (panelProps.length) {
        const sortedGroups = asArray(panelPropertiesGroups)
            .slice()
            .sort((a, b) => (a.order || 0) - (b.order || 0));
        const validGroupIds = new Set(sortedGroups.map(group => group.id));

        const propertiesByGroup = new Map();
        panelProps.forEach(property => {
            // Route properties whose group was deleted into the fallback bucket.
            const groupId = property.group_id && validGroupIds.has(property.group_id) ? property.group_id : 'ungrouped';
            if (!propertiesByGroup.has(groupId)) propertiesByGroup.set(groupId, []);
            propertiesByGroup.get(groupId).push(property);
        });

        sortedGroups.forEach(group => {
            const groupProps = (propertiesByGroup.get(group.id) || [])
                .slice()
                .sort((a, b) => (a.order || 0) - (b.order || 0));
            if (!groupProps.length) return;
            sections.push({
                id: `panel_group_${group.id}`,
                name: `Panel Property: ${group.name}`,
                items: groupProps.map(property => ({
                    id: namespaceColumnId(property.id),
                    title: property.title,
                    checked: panelIdsSet.has(Number(property.id)),
                    type: PANEL_PROPERTY_TYPE,
                    panelPropertyId: property.id
                }))
            });
        });

        const ungrouped = (propertiesByGroup.get('ungrouped') || [])
            .slice()
            .sort((a, b) => (a.order || 0) - (b.order || 0));
        if (ungrouped.length) {
            sections.push({
                id: 'panel_group_other',
                name: 'Panel Property: Other',
                items: ungrouped.map(property => ({
                    id: namespaceColumnId(property.id),
                    title: property.title,
                    checked: panelIdsSet.has(Number(property.id)),
                    type: PANEL_PROPERTY_TYPE,
                    panelPropertyId: property.id
                }))
            });
        }
    }

    return sections;
}

/**
 * Translate a set of checked column ids (as reported by the modal) back
 * into the two storage shapes StudyPage persists:
 *
 *   - `tableColumnsHidden`: string[] — column ids hidden from the table
 *   - `panelPropertyIds`:   number[] — panel properties added to this study
 *
 * The returned arrays preserve any entries outside of what the modal
 * presented (robustness: future columns outside the modal's sections
 * shouldn't be accidentally cleared).
 */
export function resolveSavedState({ sections, checkedIds, previousHidden }) {
    const checkedSet = new Set(checkedIds);
    const presentedIds = new Set();
    const nextPanelPropertyIds = [];

    sections.forEach(section => {
        section.items.forEach(item => {
            presentedIds.add(item.id);
            if (item.type === PANEL_PROPERTY_TYPE && checkedSet.has(item.id)) {
                nextPanelPropertyIds.push(Number(item.panelPropertyId));
            }
        });
    });

    // Start from the previous hidden list, drop any presented non-panel-
    // property ids (we'll re-add the unchecked ones below), drop any
    // panel-property ids whose property isn't in the new subscription set.
    const nextPanelSet = new Set(nextPanelPropertyIds);
    const hiddenBase = asArray(previousHidden).filter(id => {
        if (id.startsWith('panel_property_')) {
            const pid = Number(id.replace('panel_property_', ''));
            return nextPanelSet.has(pid);
        }
        return !presentedIds.has(id);
    });

    sections.forEach(section => {
        section.items.forEach(item => {
            if (item.type === PANEL_PROPERTY_TYPE) return; // governed by panelPropertyIds only
            if (!checkedSet.has(item.id) && !hiddenBase.includes(item.id)) {
                hiddenBase.push(item.id);
            }
        });
    });

    return {
        tableColumnsHidden: hiddenBase,
        panelPropertyIds: nextPanelPropertyIds
    };
}
